import { useEffect, useRef, useState } from "react";
import { synthesyzeSpeech } from "../apis/speech";
import { getAudioLanguageFromLocalStorage } from "../utils/speechUtils";

export type OngoingAnnouncement = {
  bookingId: string;
  patientName: string;
  // Each transfer cycle gets a fresh id, so the same booking_id can re-announce
  // after being transferred away and back. Falsy values are treated as "no cycle".
  transferCycleId?: string | null;
  room?: string;
};

const AudioManager = (() => {
  let currentAudio: HTMLAudioElement | null = null;
  let onPermissionRequired: (() => void) | null = null;

  const setOnPermissionRequired = (cb: () => void) => {
    onPermissionRequired = cb;
  };

  const play = async (audioContent: string) => {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
    }
    currentAudio = new Audio(`data:audio/mp3;base64,${audioContent}`);
    try {
      await currentAudio.play();
      await new Promise((resolve) =>
        currentAudio?.addEventListener("ended", resolve, { once: true })
      );
      await currentAudio.play();
      await new Promise((resolve) =>
        currentAudio?.addEventListener("ended", resolve, { once: true })
      );
    } catch (error) {
      const domError = error as DOMException;
      if (
        domError.name === "NotAllowedError" ||
        domError.message?.includes("user gesture") ||
        domError.message?.includes("interact with the document")
      ) {
        if (onPermissionRequired) onPermissionRequired();
      }
      throw error;
    } finally {
      currentAudio = null;
    }
  };

  const stop = () => {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      currentAudio = null;
    }
  };

  return { play, stop, setOnPermissionRequired };
})();

const dedupKeyOf = (a: OngoingAnnouncement) =>
  `${a.bookingId}::${a.transferCycleId ?? ""}`;

const muteAllVideos = () => {
  document.querySelectorAll("video").forEach((v) => (v.muted = true));
};
const unmuteAllVideos = () => {
  document.querySelectorAll("video").forEach((v) => (v.muted = false));
};

const useSpeakOngoingQueue = (
  language: string = getAudioLanguageFromLocalStorage(),
  doctorName: string | undefined,
  ongoingList: OngoingAnnouncement[]
) => {
  const announcedRef = useRef<Set<string>>(new Set());
  const queueRef = useRef<OngoingAnnouncement[]>([]);
  const isPlayingRef = useRef<boolean>(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);

  const getAudioPermission = () =>
    localStorage.getItem("audioPermission") === "allowed";
  const setAudioPermission = (allowed: boolean) =>
    localStorage.setItem("audioPermission", allowed ? "allowed" : "denied");

  const drain = async () => {
    if (isPlayingRef.current) return;
    isPlayingRef.current = true;

    while (queueRef.current.length > 0) {
      const next = queueRef.current.shift()!;
      const key = dedupKeyOf(next);
      if (announcedRef.current.has(key)) continue;

      if (!doctorName || !next.patientName) {
        announcedRef.current.add(key);
        continue;
      }

      try {
        const res = await synthesyzeSpeech(
          language,
          doctorName.toLowerCase(),
          next.patientName.toLowerCase(),
          next.bookingId,
          next.room
        );
        if (res?.status !== 200) {
          // Don't mark as announced — let next poll retry.
          continue;
        }

        if (!getAudioPermission()) {
          setShowPermissionModal(true);
          // Re-queue to retry once permission is granted.
          queueRef.current.unshift(next);
          break;
        }

        muteAllVideos();
        await AudioManager.play(res.data.result);
        announcedRef.current.add(key);
      } catch (err) {
        const domError = err as DOMException;
        if (
          domError?.name === "NotAllowedError" ||
          domError?.message?.includes("user gesture") ||
          domError?.message?.includes("interact with the document")
        ) {
          setShowPermissionModal(true);
          queueRef.current.unshift(next);
          break;
        }
        // On other errors, drop this item — don't block the queue forever.
        announcedRef.current.add(key);
      } finally {
        unmuteAllVideos();
      }
    }

    isPlayingRef.current = false;
  };

  // Stable signature for deps: list of dedup keys joined.
  const signature = ongoingList.map(dedupKeyOf).join("|");

  useEffect(() => {
    if (!doctorName) return;
    const fresh = ongoingList.filter(
      (a) =>
        a.bookingId &&
        a.patientName &&
        !announcedRef.current.has(dedupKeyOf(a)) &&
        !queueRef.current.some((q) => dedupKeyOf(q) === dedupKeyOf(a))
    );
    if (fresh.length === 0) return;
    queueRef.current.push(...fresh);
    drain();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, doctorName, language]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      AudioManager.stop();
      unmuteAllVideos();
    };
  }, []);

  // Permission handlers
  useEffect(() => {
    AudioManager.setOnPermissionRequired(() => setShowPermissionModal(true));
  }, []);

  const handleAllowAudio = () => {
    setAudioPermission(true);
    setShowPermissionModal(false);
    drain();
  };
  const handleCancelAudio = () => {
    setAudioPermission(false);
    setShowPermissionModal(false);
    queueRef.current = [];
  };

  return { showPermissionModal, handleAllowAudio, handleCancelAudio };
};

export default useSpeakOngoingQueue;
